import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
  Modal,
} from "react-bootstrap";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Api } from "./Data/Api";
import { useParamIds } from "./Data/Common";
import { toast } from "react-toastify";
import { ParkerImportReq, Plan } from "./Data/ApiTypes";
import { UserPaymentType, UserRole } from "./Data/ApiTransport";
import { QuestionCircle } from "react-bootstrap-icons";

interface ValidationError {
  row: number;
  reason: string;
  raw: string;
}

export function FacilityParkerImportPage() {
  const { facilityId } = useParamIds();
  const [file, setFile] = useState<File | null>(null);
  const [validating, setValidating] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [validated, setValidated] = useState(false);
  const [importing, setImporting] = useState(false);
  const [planList, setPlanList] = useState<Plan[]>([]);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    const fetchPlans = async () => {
      const result = await Api.facilityPlanList(facilityId);
      if (!result.isOk) {
        toast.error("Failed to fetch plans");
        return;
      }

      setPlanList(result.value);
    };

    fetchPlans();
  }, [facilityId]);

  const handleDownloadTemplate = () => {
    const header = [
      "Name",
      "Email",
      "Phone Num",
      "Spaces",
      "Plan Id",
      "Plan Rate",
      "Start Date",
      "Payment Type",
    ];

    const sampleRow = [
      "John Doe",
      "john.doe@example.com",
      "123-456-7890",
      "2",
      "1",
      "200.00",
      "06/19/2025",
      "Invoiced",
    ];

     const sampleRow2 = [
      "John Doe",
      "john.doe@example.com",
      "123-456-7890",
      "2",
      "1",
      "200.00",
      "06/19/2025",
      "Recurring",
    ];
    

    const csvContent = [header, sampleRow, sampleRow2]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "SpotSync-parker-import-template.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadListOfPlans = async () => {
    const header = ["Id", "Name"];

    const rows = planList.map((plan) => [plan.planId, plan.name]);

    const csvContent = [header, ...rows]
      .map((row) => row.map((field) => `"${field}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "SpotSync-plan-list.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const showInstructions = () => {
    setShowInstructionsModal(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = e.target.files?.[0];
    setFile(uploadedFile ?? null);
    setValidated(false);
    setErrors([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;

    setValidating(true);
    try {
      const text = await file.text();
      const rows = text
        .split("\n")
        .map(line => {
          return line
            .split(",")
            .map(field => {
              const cleaned = field.trim();
              return cleaned.startsWith('"') && cleaned.endsWith('"')
                ? cleaned.slice(1, -1).trim()
                : cleaned;
            });
        })
        .filter(row => row.some(field => field.length > 0)); 

      const [header, ...dataRows] = rows;

      const expectedHeaders = [
        "Name",
        "Email",
        "Phone Num",
        "Spaces",
        "Plan Id",
        "Plan Rate",
        "Start Date",
        "Payment Type",
      ];

      const headerMismatch =
        header.length !== expectedHeaders.length ||
        header.some(
          (h, i) => h.toLowerCase() !== expectedHeaders[i].toLowerCase()
        );

      if (headerMismatch) {
        setErrors([
          {
            row: 0,
            reason: `CSV headers are incorrect. Expected: ${expectedHeaders.join(
              ", "
            )}`,
            raw: header.join(","),
          },
        ]);
        return;
      }

      const errors = dataRows
        .map((fields, index) => {
          if (fields.length !== expectedHeaders.length) {
            return {
              row: index + 2,
              reason: `Expected ${expectedHeaders.length} fields but got ${fields.length}.`,
              raw: fields.join(","),
            };
          }

          const [name, email, phone, spaces, planId, planRate, startDate, paymentType] =
            fields;

          const rowErrors: string[] = [];

          if (!name) rowErrors.push("Missing Name.");
          if (!/\S+@\S+\.\S+/.test(email))
            rowErrors.push("Email is missing or invalid.");
          if (isNaN(parseInt(spaces)))
            rowErrors.push("Spaces is missing or invalid.");
          const planIds = new Set(planList.map((p) => String(p.planId)));
          if (
            !planId ||
            (!planIds.has(planId.trim()))
          )
            rowErrors.push("Plan ID is missing or invalid.");
          if (isNaN(parseFloat(planRate)))
            rowErrors.push("Plan Rate is missing or invalid.");
          const plan = planList.find(
            (plan) => plan.planId === parseInt(planId)
          );

          const planRateCents = Math.round(parseFloat(planRate) * 100);

          if (
            plan &&
            !plan.prices.some(
              (p) => p.price.toJSON().amount === planRateCents
            ) 
          ) {
            rowErrors.push("The Plan Rate does not exist for that plan.");
          }
        
          if (!startDate) {
            rowErrors.push("Missing Start Date.");
          } else {
            const dateRegex = /^\d{1,2}\/\d{1,2}\/\d{4}$/;

            if (!dateRegex.test(startDate)) {
              rowErrors.push("Start Date must be in format M/d/yyyy or MM/dd/yyyy.");
            } else {
              const parsed = new Date(startDate);
              if (isNaN(parsed.getTime())) {
                rowErrors.push("Invalid Start Date.");
              }
            }
          }

          if (!Object.values(UserPaymentType).includes(paymentType as unknown as UserPaymentType))
            rowErrors.push("Invalid Payment Type (must be Invoiced or Recurring).");

          if (rowErrors.length > 0) {
            return {
              row: index + 2,
              reason: rowErrors.join(" "),
              raw: fields.join(","),
            };
          }

          return null;
        })
        .filter(Boolean) as { row: number; reason: string; raw: string }[];

      if (errors.length > 0) {
        setErrors(errors);
        return;
      }

      setValidated(true);
    } catch (err) {
      toast.error("Error reading CSV file.");
    } finally {
      setValidating(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleImport = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) return;

    setImporting(true);
    try {
      var req: ParkerImportReq = {
        file: file,
      };
      var result = await Api.parkerImport(facilityId, req);
      if (!result.isOk) {
        toast.error("Failed to import.");
        return;
      }
      toast.success(
        "Import started! You’ll get an email report when it finishes."
      );
      setValidated(false);
      setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      console.error("Import failed", err);
      toast.error("Import failed.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-3">
        <Col>
          <Button variant="link" onClick={() => nav(-1)}>
            ← Back
          </Button>
        </Col>
      </Row>

      <Row className="mb-2">
        <Col md={8}>
          <h2>Bulk Import Parkers</h2>
          <p className="text-muted">
            Use this tool to import parkers from another system into SpotSync.
            Imported parkers will be added to SpotSync and can be managed like
            any other user. This helps carry over account history and ensures
            accurate billing going forward.
          </p>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col>
          <Button
            variant="outline-primary"
            onClick={handleDownloadTemplate}
            className="me-3"
          >
            Download Import Template
          </Button>
          <Button variant="outline-primary" onClick={handleDownloadListOfPlans}>
            Download List of Plans
          </Button>
          <Button className="mx-5" variant="outline-primary" onClick={showInstructions}>
            <QuestionCircle className="me-2" style={{ marginTop: "-4px" }} />
            Instructions
          </Button>
        </Col>
      </Row>

      <Form onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group controlId="csvFile">
              <Form.Label>Upload completed template (.csv)</Form.Label>
              <Form.Control
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                required
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-3">
          <Col md={6}>
            <Button
              variant={validated ? "success" : "primary"}
              type="submit"
              disabled={
                !file || validating || fileInputRef?.current?.value == ""
              }
              className="w-100"
            >
              {validating ? (
                <>
                  <Spinner size="sm" animation="border" /> Validating…
                </>
              ) : validated && file ? (
                `${file.name} is validated!`
              ) : (
                "Validate"
              )}
            </Button>
          </Col>
        </Row>
      </Form>

      {errors.length > 0 && (
        <Row className="mt-5">
          <Col>
            <Alert variant="danger">
              <strong>Validation failed.</strong> Correct the issues below and
              re‑upload.
            </Alert>
            <Table bordered striped hover size="sm">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Reason</th>
                  <th>Row data</th>
                </tr>
              </thead>
              <tbody>
                {errors.flatMap((err, i) =>
                  err.reason
                    .split(". ")
                    .filter(Boolean)
                    .map((msg, j) => (
                      <tr key={`${i}-${j}`}>
                        <td>{err.row}</td>
                        <td style={{ whiteSpace: "pre-wrap" }}>
                          {msg.trim().replace(/\.$/, "")}
                        </td>
                        <td style={{ fontFamily: "monospace" }}>{err.raw}</td>
                      </tr>
                    ))
                )}
              </tbody>
            </Table>
          </Col>
        </Row>
      )}

      {errors.length === 0 && validated && (
        <Form className="mt-5" onSubmit={handleImport}>
          <Row>
            <Col md={6}>
              <Button type="submit" className="w-100" disabled={importing}>
                {importing ? (
                  <>
                    <Spinner size="sm" animation="border" /> Importing…
                  </>
                ) : (
                  "Run Import"
                )}
              </Button>
            </Col>
          </Row>
        </Form>
      )}

      <Modal show={showInstructionsModal} onHide={() => setShowInstructionsModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Import Instructions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <strong>Name:</strong> Required
            <div className="text-muted small">Full name of the parker or owner</div>
          </div>
          <div className="mb-3">
            <strong>Spaces:</strong> Required
            <div className="text-muted small">Number of parking spaces assigned to this user</div>
          </div>
          <div className="mb-3">
            <strong>Start Date:</strong> Required. Expected format (MM/dd/yyyy) i.e. 4/23/2025
            <div className="text-muted small">Date when the user's account should become active</div>
          </div>
          <div className="mb-3">
            <strong>Plan Rate:</strong> Required 
            <div className="text-muted small">Monthly rate for the parking plan (e.g., 200.00). This price must exist for the plan.</div>
          </div>
          <div className="mb-3">
            <strong>Plan Id:</strong> Required
            <div className="text-muted small">Available plan IDs can be downloaded from the "Download List of Plans" button</div>
          </div>
          <div className="mb-3">
            <strong>Phone Num:</strong> Optional
            <div className="text-muted small">Contact phone number (e.g., 123-456-7890)</div>
          </div>
          <div className="mb-3">
            <strong>Email:</strong> Required
            <div className="text-muted small">Primary email address for account communications</div>
          </div>
         
          <div className="mb-3">
            <strong>Payment Type:</strong> Required   "Invoiced" or "Recurring"
            <div className="text-muted small">"Invoiced" for manual billing or "Recurring" for automatic payments. The user will be emailed an invitation to set up recurring payments or an invoice for future payment.</div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInstructionsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
